import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { BrowserRouter, useHistory } from "react-router-dom";
import "office-ui-fabric-react/dist/css/fabric.css";
import { themes } from "./components/ThemeContext";
import { ThemeProvider } from "@fluentui/react-theme-provider";
import Header from "./components/Header";
import Footer from "./components/Footer";
import MainMenu from "./components/MainMenu";
import { insightsInstrumentationKey, nodeEnv, proxyUrl, serverHost } from "./config";
import GlobalStyle from "./components/GlobalStyle";
import Auth from "./Auth/Auth";
import "./App.sass";
import { connect, useDispatch } from "react-redux";
import { isAuthenticateRedux, loginRedux, completeUserDataRedux, checkSuscriptionsRedux, userLastPlanActive, setSavingsPlanElementsStatus } from "./store/auth/actions";
import { getAppInsights } from './services/TelemetryService';
import TelemetryProvider from './providers/telemetry-provider';
import ManagedPortfolioService from './services/ManagedPortfolioService';
import UserIdentitiesService from './services/UserIdentitiesService';
import styled from 'styled-components';
import { setTheme, setConnectionReference, refreshStatusBar } from "./store/ui/actions";
import AutoLogoutLogic from "./AutoLogoutLogic";
import { HubConnectionBuilder, HubConnection } from '@microsoft/signalr';
import { setIconOptions } from '@fluentui/react/lib/Styling';
import CartChip from "./components/CartChip/CartChip";
import CustomLoader from "./screen/Shared/CustomLoader";
import { initializeIcons } from "@fluentui/react/lib/Icons";

// Suppress icon warnings.
setIconOptions({disableWarnings: true});
initializeIcons(undefined, {disableWarnings: true});

const MainGrid = styled.div.attrs((props: any) =>
({
    footerVisible: props.footerVisible
}))`
${(props: any) =>
{
    return props.footerVisible ? "" : "padding-bottom: 0 !important";
}}`;

function mapStateToProps(state: any)
{
    return{
        footerVisible: state.ui.footerVisible,
        // Cambiar nombre a iFrame theme o algo similar. Separar del que se almacena en local storage?
        storeTheme: state.ui.theme,
    };
}

const App: (props: any) => any = ({children, footerVisible, storeTheme}) =>
{
    const dispatch = useDispatch();
    const localTheme = window.localStorage.getItem("theme") || "light";
    window.localStorage.setItem("theme", localTheme);
    const [theme, setStateTheme] = useState(
    {
        name: localTheme,
        palette: localTheme === "dark" ? themes.dark : themes.light,
    });
    const menuRef = useRef();
    const [isLoaded, setIsLoaded] = useState(false);
    const [connection, setConnection] = useState<HubConnection>();
    const [connectionReferenceAux, setConnectionReferenceAux] = useState<string>();
    let history = useHistory();
    const iframeVersion = useState(history.location.pathname.match(/iframe/));
    let appInsights: any = null;

    const checkUser = useCallback(async () =>
    {
        await dispatch(setTheme(localTheme));
        let user: any = Auth.getUserProfile();
        if(user === null || user.oid === undefined)
        {
            var response = await axios.get(`${serverHost}/user`, {withCredentials: true});

            if(typeof response.data.oid === "undefined")
            {
                await dispatch(isAuthenticateRedux(false));
                // Esta redirección ahora se hace desde ProtectedRoute.
                //window.location.href = `${config.server_host}/login`;
                if(iframeVersion)
                {
                    createConnection(null);
                }
            }
            else
            {
                Auth.setUserProfile(response.data);
                await dispatch(loginRedux(response.data));
                await dispatch(isAuthenticateRedux(true));
                await dispatch(completeUserDataRedux(response.data.oid));
                user = Auth.getUserProfile();

                // Comprobación de caducidad del KYC, una sola vez por login: este bloque solo se ejecuta
                // cuando no había perfil en caché, así que el "una vez" sale del propio flujo, sin marcas en
                // BD. Deliberadamente SIN await (no debe retrasar la pintura) y con el fallo silenciado: si
                // esto se cae, el arranque de la aplicación no se entera y se reintenta en el siguiente login.
                // Al terminar se refresca la barra de estado: la comprobación puede haber creado avisos
                // DESPUÉS de que la barra los pidiera, y sin esto no aparecerían hasta la siguiente carga.
                UserIdentitiesService.checkKycExpiration(user.id)
                    .then(() => dispatch(refreshStatusBar()))
                    .catch(() => {});

                await dispatch(checkSuscriptionsRedux(user.id));
                await dispatch(userLastPlanActive(user.id));
                let activePlan: any = Auth.getActivePlan();
                await dispatch(setSavingsPlanElementsStatus(activePlan.id ?? activePlan.planId));
                createConnection(user);
            }
        }
        setIsLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, localTheme]);

    useEffect(() =>
    {
        if(!isLoaded)
        {
            checkUser();
            setStateTheme(
            {
                name: localTheme,
                palette: localTheme === "dark" ? themes.dark : themes.light,
            });
            initializeIcons(undefined, { disableWarnings: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() =>
    {
		if(isLoaded && nodeEnv !== 'development')
        {
			const script = document.createElement("script");
			script.type = "text/javascript";
			script.async = true;
			script.innerHTML = `window.clarity("set", "Id Usuario", "${Auth.getUserProfile().id}"); window.clarity("set", "Nombre Usuario", "${
				Auth.getUserProfile().fullName
			}");`;
			document.body.appendChild(script);
            //console.log(Auth.getUserProfile().id, Auth.getUserProfile().fullName);
			return () =>
            {
				// clean up the script when the component in unmounted
				document.body.removeChild(script);
			};
		}
	}, [isLoaded]);

    useEffect(() =>
    {
        if(connection)
        {
            connection.start().then(() =>
            {
                connection.on('newMessage', (message: any) =>
                {
                    localStorage.setItem('modelPortfolios', message);
                    const event = new Event('storageChange')
                    document.dispatchEvent(event)
                });

                if(localStorage.getItem('modelPortfolios') === null)
                {
                    if(connectionReferenceAux)
                    {
                        ManagedPortfolioService.getManagedPortfoliosForTheCover(connectionReferenceAux);
                    }
                }
            }).catch((e: any) => console.log('Connection failed: ', e));
        }
        else
        {
            var user = Auth.getUserProfile();
            if(user.id !== undefined)
            {
                createConnection(user);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connection]);

    useEffect(() =>
    {
        if(connectionReferenceAux)
        {
            const host = window.location.hostname === 'localhost'
                ? 'http://localhost:7043'
                : proxyUrl;

            const newConnection = new HubConnectionBuilder().withUrl(`${host}/managedPortfoliosHub`,
            {
                headers: { "x-application-user-id": connectionReferenceAux }
            })
            .withAutomaticReconnect()
            .build();

            setConnection(newConnection);
        }
    }, [connectionReferenceAux]);

    const createConnection = async (user: any) =>
    {
        if(connectionReferenceAux === undefined)
        {
            var connectionReference;
            if(user?.id !== undefined)
            {
                connectionReference = user.id;
            }
            else
            {
                var hash = [...Array(32)].map(i => (~~(Math.random() * 36)).toString(36)).join('');
                connectionReference = hash;
            }
            setConnectionReferenceAux(connectionReference);
            dispatch(setConnectionReference(connectionReference));
        }
    }

    if(storeTheme !== theme.name)
    {
        setStateTheme(
        {
            name: storeTheme,
            palette: storeTheme === "dark" ? themes.dark : themes.light,
        });
    }
    const toggleTheme = async () =>
    {
        window.localStorage.setItem("theme", theme.palette.name === "dark" ? "light" : "dark");
        setStateTheme(
        {
            name: window.localStorage.theme,
            palette: window.localStorage.theme === "dark" ? themes.dark : themes.light,
        });
        dispatch(setTheme(window.localStorage.theme));
    };

    return(
        <BrowserRouter>
            {!isLoaded &&
            (
                <CustomLoader text="TU PERSONAL STORE FINANCIERO" />
            )}
            {isLoaded &&
            (
                <TelemetryProvider instrumentationKey={insightsInstrumentationKey} after={() => {appInsights = getAppInsights()}}>
                    <ThemeProvider theme={theme}>
                        <GlobalStyle theme={theme} />
                        <MainGrid className="ms-Grid" dir="ltr" footerVisible={footerVisible}>
                            {<Header toggleTheme={toggleTheme} theme={theme} menuRef={menuRef} />}
                            <div className="ms-Grid-row">
                                <div id="main-element" className="ms-Grid-col ms-sm12 ms-xl12">
                                    {children}
                                </div>
                            </div>
                            <MainMenu theme={{theme, toggleTheme}} ref={menuRef} />
                            <AutoLogoutLogic timeout={899} />
                            <Footer />
                            <CartChip />
                        </MainGrid>
                    </ThemeProvider>
                </TelemetryProvider>
            )}
        </BrowserRouter>
    );
};

export default connect(mapStateToProps)(App);